/**
 * Console-side API helpers + types for the Reactive Visualization node
 * (ADR-0056). Mirrors the Hub types in
 * atlas-hub/services/hub/src/reportRuntime/types.ts.
 *
 * Types that appear in page filter / page builder flows live in
 * `pageFilters.ts` alongside the existing `PageFilter` type.
 */

import { apiFetch } from "./api.js";

// ──────────────────────────────────────────────────────────────────────
// Types — local mirrors of ReactiveParam + ReactiveVisualizationSpec.
// We keep these in console rather than sharing via @atlas/protocol because
// the hub's type file also re-exports runtime types (TransformResult,
// RenderModel, etc.) that have no use on the client.
// ──────────────────────────────────────────────────────────────────────

export type ReactiveParamType = "text" | "text_array" | "number" | "identifier";

export type ReactiveParamNullBehavior = "all_rows" | "empty";

export type ReactiveParam = {
  name: string;
  type: ReactiveParamType;
  null_behavior: ReactiveParamNullBehavior;
  /** REQUIRED when type === "identifier". */
  allowed_values?: string[];
};

export type ReactiveVisualizationSpec = {
  specVersion: "reactive_visualization.v1";
  sql: string;
  parameters: ReactiveParam[];
  viz: { option: unknown; vizType?: string };
  rowSelection?: "none" | "single" | "multi";
};

/**
 * Incoming parameter value for a reactive-execute request. Matches the hub's
 * ReactiveParamInput shape.
 */
export type ReactiveParamInput = string | string[] | number | null;

// Shape of render models that feed ECharts / KPI / tantable renderers.
// Intentionally a loose type here — the live page widget renderers already
// discriminate this structurally. If console gains a shared RenderModel
// type later, switch to that.
export type RenderModel = Record<string, unknown>;


// ──────────────────────────────────────────────────────────────────────
// API functions
// ──────────────────────────────────────────────────────────────────────

/**
 * Execute a reactive_visualization node against its cached upstream input
 * with the given parameter values. Returns a RenderModel the widget can
 * swap in without persisting anything server-side.
 *
 * ADR-0056 §6.4 / Step 6.2. The optional `signal` aborts the in-flight
 * request when a newer slicer change supersedes this one (Step 9.3).
 */
export function reactiveExecuteNode(
  opId: string,
  nodeId: string,
  params: Record<string, ReactiveParamInput>,
  signal?: AbortSignal
): Promise<RenderModel> {
  return apiFetch<RenderModel>(
    `/api/tenant/operations/${encodeURIComponent(opId)}/nodes/${encodeURIComponent(nodeId)}/reactive-execute`,
    { method: "POST", body: { params }, signal }
  );
}

// ADR-0120: fetchCachedInput deleted (dead code — /cached-input retired;
// full-data export is the node-ref workbook route).
